/**
 * F17 (Wave 2C) — Admin force-transition confirmation Modal.
 *
 * super_admin only. Lets the operator bypass the F17 state-machine guard on
 * EITHER the lifecycle column OR the payment column. Mandatory `reason` is
 * audit-logged via F06's `onSend` hook.
 *
 * We compose `Modal` directly (instead of `Dialog`) because the operator-facing
 * confirm button needs to disable on invalid Zod parse — `Dialog` doesn't expose
 * a `confirmDisabled` prop today.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  FormField,
  LifecycleChip,
  Modal,
  PaymentStatusChip,
  SegmentedControl,
  Select,
  Textarea,
} from '@bravemove/ui-web';
import type { LifecycleState, PaymentStatus as ChipPaymentStatus } from '@bravemove/ui-web';
import {
  BOOKING_STATUS_VALUES,
  FORCE_TRANSITION_TARGETS,
  ForceTransitionInputSchema,
  PAYMENT_STATUS_VALUES,
  type BookingDetail,
  type ForceTransitionInput,
  type ForceTransitionTarget,
} from '@bravemove/types';

export interface ForceTransitionDialogProps {
  readonly booking: BookingDetail;
  readonly open: boolean;
  readonly onConfirm: (input: ForceTransitionInput) => Promise<void>;
  readonly onClose: () => void;
}

function lifecycleOptions(): ReadonlyArray<{ readonly value: string; readonly label: string }> {
  return BOOKING_STATUS_VALUES.map((s) => ({ value: s, label: s }));
}

function paymentOptions(): ReadonlyArray<{ readonly value: string; readonly label: string }> {
  return PAYMENT_STATUS_VALUES.map((s) => ({ value: s, label: s }));
}

export function ForceTransitionDialog({
  booking,
  open,
  onConfirm,
  onClose,
}: ForceTransitionDialogProps) {
  const { t } = useI18n();
  const [target, setTarget] = useState<ForceTransitionTarget>('lifecycle');
  const [to, setTo] = useState<string>(BOOKING_STATUS_VALUES[0]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset state every open so a previous abort doesn't leak into the next attempt.
  // The cascading render is intentional and bounded — three setState calls
  // collapse into a single React 18+ batched update.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setTarget('lifecycle');
    setTo(BOOKING_STATUS_VALUES[0]);
    setReason('');
  }, [open]);

  // When the operator flips the target, default `to` to the new enum's first value.
  useEffect(() => {
    if (target === 'lifecycle') setTo(BOOKING_STATUS_VALUES[0]);
    else setTo(PAYMENT_STATUS_VALUES[0]);
  }, [target]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const options = target === 'lifecycle' ? lifecycleOptions() : paymentOptions();

  /**
   * Normalised preview value — when the operator flips the target SegmentedControl
   * the `to` state is still the previous target's first value for a single
   * render before the resetting useEffect fires. Falling back to the active
   * target's first value keeps the preview chip's `state` prop in-domain
   * (otherwise the chip's `STATE_STYLE[state]` lookup returns undefined and
   * the dialog crashes mid-render).
   */
  const previewTo: string =
    target === 'lifecycle'
      ? BOOKING_STATUS_VALUES.includes(to as (typeof BOOKING_STATUS_VALUES)[number])
        ? to
        : BOOKING_STATUS_VALUES[0]
      : PAYMENT_STATUS_VALUES.includes(to as (typeof PAYMENT_STATUS_VALUES)[number])
        ? to
        : PAYMENT_STATUS_VALUES[0];

  const validation = useMemo(
    () => ForceTransitionInputSchema.safeParse({ target, to, reason }),
    [target, to, reason],
  );

  const confirmDisabled = submitting || !validation.success;

  const submit = async () => {
    if (!validation.success) return;
    setSubmitting(true);
    try {
      await onConfirm(validation.data);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('booking:admin.force_transition.title')}
      description={t('booking:admin.force_transition.description')}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="force-transition-cancel"
          >
            {t('booking:admin.force_transition.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={submitting}
            disabled={confirmDisabled}
            data-testid="force-transition-confirm"
          >
            {t('booking:action.force_transition')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="force-transition-dialog">
        <div data-testid="force-transition-warning">
          <Banner tone="warning" title={t('booking:admin.force_transition.warning_title')}>
            {t('booking:admin.force_transition.warning_body')}
          </Banner>
        </div>

        {/* From → To preview header — surfaces the impending transition. */}
        <div
          className="flex items-center gap-2 flex-wrap rounded-md bg-surface-2 px-3 py-2"
          data-testid="force-transition-preview"
        >
          <span className="text-[10.5px] font-mono uppercase tracking-wider text-ink-3">
            {t('booking:admin.force_transition.target_label')}
          </span>
          {target === 'lifecycle' ? (
            <LifecycleChip
              state={booking.status as LifecycleState}
              data-testid="force-transition-preview-from"
            />
          ) : (
            <PaymentStatusChip
              state={booking.paymentStatus as ChipPaymentStatus}
              data-testid="force-transition-preview-from"
            />
          )}
          <span aria-hidden="true" className="text-ink-2 font-mono">
            →
          </span>
          {target === 'lifecycle' ? (
            <LifecycleChip
              state={previewTo as LifecycleState}
              data-testid="force-transition-preview-to"
            />
          ) : (
            <PaymentStatusChip
              state={previewTo as ChipPaymentStatus}
              data-testid="force-transition-preview-to"
            />
          )}
        </div>

        <FormField label={t('booking:admin.force_transition.target_label')}>
          <SegmentedControl<ForceTransitionTarget>
            value={target}
            onChange={(v) => setTarget(v)}
            options={FORCE_TRANSITION_TARGETS.map((tk) => ({
              value: tk,
              label: t(`booking:admin.force_transition.target_${tk}`),
            }))}
            aria-label={t('booking:admin.force_transition.target_label')}
          />
        </FormField>

        <FormField label={t('booking:admin.force_transition.to_label')} required>
          <Select
            value={to}
            onChange={(e) => setTo(e.target.value)}
            options={options}
            data-testid="force-transition-to"
          />
        </FormField>

        <FormField label={t('booking:admin.force_transition.reason_label')} required>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('booking:admin.force_transition.reason_placeholder')}
            rows={3}
            maxLength={500}
            data-testid="force-transition-reason"
          />
        </FormField>

        <p className="text-[11.5px] text-ink-3 font-mono tabular-nums">
          {t('booking:admin.force_transition.booking_ref', { id: booking.id })}
        </p>
      </div>
    </Modal>
  );
}
